// As rotas do painel. Cada feature entra com `lazyDaTela()`: o Vite gera um chunk
// por tela e o navegador só baixa o que abrir. A casca (Layout) vem no chunk
// inicial; a Início é a rota inicial e é pré-carregada junto.
import { Suspense, useEffect, type ReactNode } from 'react';
import { lazyDaTela } from '../shared/recarga-de-chunk';
import { createBrowserRouter, Navigate } from 'react-router';
import { Layout } from '../features/casca/Layout';
import { liberarBoot } from '../shared/ui/boot';
import { Casca } from './providers';

const Inicio = lazyDaTela(() => import('../features/inicio/Inicio'));
const Agentes = lazyDaTela(() => import('../features/agentes/Agentes'));
const Equipe = lazyDaTela(() => import('../features/equipe/Equipe'));
const Plano = lazyDaTela(() => import('../features/plano/Plano'));
const Produtos = lazyDaTela(() => import('../features/produtos/Produtos'));
const Vitrine = lazyDaTela(() => import('../features/vitrine/Vitrine'));
const Bio = lazyDaTela(() => import('../features/vitrine/Bio'));
const Desempenho = lazyDaTela(() => import('../features/desempenho/Desempenho'));
const Configuracoes = lazyDaTela(() => import('../features/configuracoes/Configuracoes'));
const Entrega = lazyDaTela(() => import('../features/entrega/Entrega'));
const Pagamentos = lazyDaTela(() => import('../features/pagamentos/Pagamentos'));
const Avancado = lazyDaTela(() => import('../features/configuracoes/Avancado'));
const Integracoes = lazyDaTela(() => import('../features/integracoes/Integracoes'));
const Canais = lazyDaTela(() => import('../features/canais/Canais'));
const Pedidos = lazyDaTela(() => import('../features/pedidos/Pedidos'));
const Transmissao = lazyDaTela(() => import('../features/transmissao/Transmissao'));
const Automacoes = lazyDaTela(() => import('../features/automacoes/Automacoes'));
const Conhecimento = lazyDaTela(() => import('../features/conhecimento/Conhecimento'));
const Conversas = lazyDaTela(() => import('../features/conversas/Conversas'));
// CRM e Intelligence: rota registrada, item do menu COMENTADO em casca/telas.ts
// (decisao do Bruno em 2026-09-19 — migrar sem mostrar ainda).
const Crm = lazyDaTela(() => import('../features/crm/Crm'));
const Inteligencia = lazyDaTela(() => import('../features/inteligencia/Inteligencia'));
const Parceiros = lazyDaTela(() => import('../features/parceiros/Parceiros'));
const AdminDosParceiros = lazyDaTela(() => import('../features/parceiros/Admin'));
const Assinar = lazyDaTela(() => import('../features/plano/Assinar'));
// Só o super admin vê no menu; quem digitar o endereço sem ser recebe o 403 da API.
const LojasDaPlataforma = lazyDaTela(() => import('../features/super/Lojas'));
// Design: a tela de projetos fica no menu; o editor e o link compartilhado são
// tela cheia, fora da casca (o canvas precisa da janela inteira).
const Design = lazyDaTela(() => import('../features/design/Design'));
const EditorDoDesign = lazyDaTela(() => import('../features/design/Editor'));
const DesignCompartilhado = lazyDaTela(() => import('../features/design/Compartilhado'));
const AppDoDesign = lazyDaTela(() => import('../features/design/AppPublico'));
const DesignNaPlataforma = lazyDaTela(() => import('../features/design/Plataforma'));
// Estúdio: publicar no Instagram e ver o resultado dos posts.
const IdeiasDoEstudio = lazyDaTela(() => import('../features/estudio/Ideias'));
const EstrategiaDoEstudio = lazyDaTela(() => import('../features/estudio/Estrategia'));
const ReferenciasDoEstudio = lazyDaTela(() => import('../features/estudio/Referencias'));
const ConteudosDoEstudio = lazyDaTela(() => import('../features/estudio/Conteudos'));
const ConteudoDoEstudio = lazyDaTela(() => import('../features/estudio/Conteudo'));
const PublicarNoEstudio = lazyDaTela(() => import('../features/estudio/Publicar'));
const ResultadosDoEstudio = lazyDaTela(() => import('../features/estudio/Resultados'));
const Anuncios = lazyDaTela(() => import('../features/estudio/anuncios/Anuncios'));
const NovaCampanha = lazyDaTela(() => import('../features/estudio/anuncios/NovaCampanha'));
const NovaPmax = lazyDaTela(() => import('../features/estudio/anuncios/NovaPmax'));
const CampanhaDeAnuncio = lazyDaTela(() => import('../features/estudio/anuncios/Campanha'));
const Entrar = lazyDaTela(() => import('../features/entrada/Entrar'));
const CriarConta = lazyDaTela(() => import('../features/entrada/CriarConta'));
const Convite = lazyDaTela(() => import('../features/entrada/Convite'));
const SenhaNova = lazyDaTela(() => import('../features/entrada/SenhaNova'));
const ConfirmarEmail = lazyDaTela(() => import('../features/entrada/ConfirmarEmail'));
const ConectarIa = lazyDaTela(() => import('../features/conectar-ia/ConectarIa'));
const carregando = <div className="apoio" style={{ padding: 8 }} aria-busy="true">Abrindo…</div>;

/**
 * Dentro do MESMO Suspense da tela: o efeito só roda quando a tela inteira
 * montou (o chunk chegou). É quando a cortina de abertura sai — no painel
 * antigo, depois de a Início estar desenhada, e não antes.
 */
function CortinaSai() {
  useEffect(() => { liberarBoot(); }, []);
  return null;
}
const tela = (conteudo: ReactNode) => <Suspense fallback={carregando}><CortinaSai />{conteudo}</Suspense>;

export const rotas = createBrowserRouter([
  // Públicas: sem sessão, sem menu. Quem já está logado é levado pro painel.
  { path: '/app/entrar', element: tela(<Entrar />) },
  { path: '/app/criar-conta', element: tela(<CriarConta />) },
  { path: '/app/convite/:codigo', element: tela(<Convite />) },
  // Onde caem os links dos e-mails de conta (2026-09-27): no nosso domínio, em português.
  { path: '/app/senha-nova', element: tela(<SenhaNova />) },
  { path: '/app/confirmar-email', element: tela(<ConfirmarEmail />) },
  { path: '/app/design/compartilhado/:token', element: tela(<DesignCompartilhado />) },
  // O app publicado: público, sem sessão e sem menu.
  { path: '/app/design/a/:slug', element: tela(<AppDoDesign />) },
  { path: '/app/design/fluxos/:fluxoId', element: <Casca>{tela(<EditorDoDesign />)}</Casca> },
  // O consentimento do Conectar com IA (OAuth do servidor MCP): logado, sem menu.
  { path: '/app/conectar-ia', element: <Casca>{tela(<ConectarIa />)}</Casca> },
  {
    path: '/app',
    element: <Casca><Layout /></Casca>,
    children: [
      { index: true, element: tela(<Inicio />) },
      { path: 'agentes', element: tela(<Agentes />) },
      { path: 'conhecimento', element: tela(<Conhecimento />) },
      { path: 'equipe', element: tela(<Equipe />) },
      { path: 'plano', element: tela(<Plano />) },
      { path: 'produtos', element: tela(<Produtos />) },
      { path: 'vitrine', element: tela(<Vitrine />) },
      { path: 'bio', element: tela(<Bio />) },
      { path: 'desempenho', element: tela(<Desempenho />) },
      { path: 'configuracoes', element: tela(<Configuracoes />) },
      { path: 'entrega', element: tela(<Entrega />) },
      { path: 'pagamentos', element: tela(<Pagamentos />) },
      { path: 'avancado', element: tela(<Avancado />) },
      { path: 'integracoes', element: tela(<Integracoes />) },
      { path: 'canais', element: tela(<Canais />) },
      { path: 'pedidos', element: tela(<Pedidos />) },
      { path: 'transmissao', element: tela(<Transmissao />) },
      { path: 'automacoes', element: tela(<Automacoes />) },
      { path: 'crm', element: tela(<Crm />) },
      { path: 'intelligence', element: tela(<Inteligencia />) },
      { path: 'conversas', element: tela(<Conversas />) },
      { path: 'design', element: tela(<Design />) },
      { path: 'estudio/ideias', element: tela(<IdeiasDoEstudio />) },
      { path: 'estudio/estrategia', element: tela(<EstrategiaDoEstudio />) },
      { path: 'estudio/referencias', element: tela(<ReferenciasDoEstudio />) },
      { path: 'estudio/conteudos', element: tela(<ConteudosDoEstudio />) },
      { path: 'estudio/conteudos/:id', element: tela(<ConteudoDoEstudio />) },
      { path: 'estudio/publicar', element: tela(<PublicarNoEstudio />) },
      { path: 'estudio/resultados', element: tela(<ResultadosDoEstudio />) },
      { path: 'estudio/anuncios', element: tela(<Anuncios />) },
      { path: 'estudio/anuncios/nova', element: tela(<NovaCampanha />) },
      { path: 'estudio/anuncios/google/nova', element: tela(<NovaPmax />) },
      { path: 'estudio/anuncios/campanhas/:id', element: tela(<CampanhaDeAnuncio />) },
      { path: 'parceiros', element: tela(<Parceiros />) },
      { path: 'parceiros/admin', element: tela(<AdminDosParceiros />) },
      { path: 'assinar', element: tela(<Assinar />) },
      { path: 'super', element: tela(<LojasDaPlataforma />) },
      { path: 'super/design', element: tela(<DesignNaPlataforma />) },
      { path: '*', element: <Navigate to="/app" replace /> },
    ],
  },
]);
